import React, {useCallback, useMemo, useRef, useState} from 'react';
import {List, Map} from 'immutable';
import cn from 'classnames';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import {WhistleIcon} from '@components/icon/WhistleIcon';
import {ThumbUpIcon} from '@components/icon/ThumbUpIcon';
import {ThumbDownIcon} from '@components/icon/ThumbDownIcon';
import './CallCardHeader.sass';
import {Call} from '@store/call-card/Call';
import CallCardActions from '@store/call-card/CallCardActions';
import LibraryTagDisplay from 'components/pure/call-card/LibraryTagDisplay';
import CallActionDialogs from '@components/call-card/CallActionDialogs';
import Badge from '@material-ui/core/Badge';
import {createStyles, withStyles} from '@material-ui/core/styles';
import Scorecard from 'records/Scorecard';
import {UserComment} from 'records/UserComment';
import {User, UserId} from 'records/User';
import {browserHistory} from 'react-router';
import {LeftArrowLong} from '@components/icon/LeftArrowLong';
import {CallCardVariant} from '@components/call-card/CallCard.types';
import {UserAvatar} from '@components/util/UserAvatar';
import {CallCardHeaderActions} from '@components/call-card/CallCardHeaderActions';
import {CallCoachingStatus} from './CallHeader.utils';

interface CallCardHeaderProps {
  call: Call;
  scorecards: List<Scorecard>;
  comments: Map<UserId, List<UserComment>>;
  sessionUser: User;
  variant: CallCardVariant;
  supportsCrmSync: boolean;
}

export const CallCardHeader: React.FC<CallCardHeaderProps> = ({
  call,
  scorecards,
  comments,
  sessionUser,
  variant,
  supportsCrmSync,
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const [voteLoading, setVoteLoading] = useState(false);
  const caller = call.caller;
  const onVote = useCallback(
    async (vote: 'Up' | 'Down') => {
      if (voteLoading) return;
      try {
        setVoteLoading(true);
        call.vote === vote
          ? await CallCardActions.deleteVote(call.id)
          : await CallCardActions.setVote(call.id, vote);
      } finally {
        setVoteLoading(false);
      }
    },
    [call.vote, call.id, setVoteLoading, voteLoading]
  );

  const coachingStatus = useMemo(() => {
    // @ts-ignore Scorecard is not typescript yet. TODO fix that
    const hasScores = scorecards.some(c => c.results.some(r => r.reviewer_user_id !== sessionUser));
    const hasComments = comments.some((_, userId) => userId !== sessionUser.id);

    if (hasScores || hasComments) return CallCoachingStatus.PROVIDED;
    if (call.coachMe) return CallCoachingStatus.REQUESTED;
    return CallCoachingStatus.NONE;
  }, [scorecards, call, comments, sessionUser]);

  return (
    <header ref={headerRef} className="call-card-header-wrapper">
      {variant !== CallCardVariant.INBOX && (
        <Button
          className="back-link"
          onClick={() => {
            if (variant === CallCardVariant.DEFAULT) {
              browserHistory.push({pathname: '/calls/my', state: {dontReset: true}});
            } else {
              browserHistory.goBack();
            }
          }}>
          <div className="back-icon">
            <LeftArrowLong />
          </div>
          Back{variant === CallCardVariant.DEFAULT ? ' to Search' : ''}
        </Button>
      )}
      <div className="call-card-header">
        <div className="call-info">
          <div className="caller">
            <UserAvatar user={caller} />
            <h1>{caller.getFullName()}</h1>
            {coachingStatus !== CallCoachingStatus.NONE && (
              <span style={{color: `#${caller.color}`}} className="coaching-status">
                <WhistleIcon fill={`#${caller.color}`} tooltip="Coaching status" />
                {coachingStatus.toLowerCase()}
              </span>
            )}
          </div>
          <div className="template-one">
            {call.template.lineOne}
            <IconButton disabled={voteLoading} size="small" onClick={() => onVote('Up')}>
              <StyledBadge
                className={cn('badge', 'vote-up', {active: call.vote === 'Up'})}
                badgeContent={call.votesUp}>
                <ThumbUpIcon tooltip="Upvote" />
              </StyledBadge>
            </IconButton>
            <IconButton disabled={voteLoading} size="small" onClick={() => onVote('Down')}>
              <StyledBadge
                className={cn('badge', 'vote-down', {active: call.vote === 'Down'})}
                badgeContent={call.votesDown}>
                <ThumbDownIcon tooltip="Downvote" />
              </StyledBadge>
            </IconButton>
          </div>
          <div>{call.template.lineTwo}</div>
        </div>
        <div className="call-header-right">
          <CallCardHeaderActions
            supportsCrmSync={supportsCrmSync}
            call={call}
            sessionUser={sessionUser}
            headerRef={headerRef.current}
          />
          <LibraryTagDisplay fileUploadId={call.id} />
        </div>
        <CallActionDialogs call={call} />
      </div>
    </header>
  );
};

const StyledBadge = withStyles(theme =>
  createStyles({
    badge: {
      padding: '2px',
      width: 16,
      height: 16,
      minWidth: 16,
    },
  })
)(Badge);
